import uuid from '@openphone/lib/uuid'
import { LRUCache } from 'lru-cache'

import sound from '@ui/Sound/sound'

import type AppStore from './AppStore'

interface NotificationHandlers {
  onShow?: () => void
  onFailed?: (error: string | Error) => void
  onClose?: () => void
  onClick?: () => void
  onReply?: (reply: string) => void
  onAction?: (index: number) => void
}

interface NotificationOptions extends NotificationHandlers {
  id?: string
  title: string
  body: string
  silent?: boolean
  hasReply?: boolean
  replyPlaceholder?: string
  soundName?: 'missed-call' | 'new-message' | 'default'
}

export default class NotificationController {
  private nativeHandlers = new LRUCache<string, NotificationHandlers>({
    max: 10,
    ttl: 60_000,
  })

  constructor(private app: AppStore) {
    this.registerListeners()
  }

  get isSupported() {
    return this.app.isElectron || 'Notification' in window
  }

  get permission() {
    return window.Notification?.permission
  }

  get playNotificationSounds() {
    return this.app.service.user.current?.settings?.web.playNotificationSounds ?? true
  }

  requestPermission(): Promise<NotificationPermission> {
    return window.Notification?.requestPermission() ?? Promise.resolve('denied')
  }

  show(opts: NotificationOptions): () => void {
    void this.playSound(opts)
    if (this.isSupported) {
      if (this.app.isElectron) {
        return this.showNative(opts)
      } else {
        return this.showWeb(opts)
      }
    } else {
      return () => null
    }
  }

  private showNative(opts: NotificationOptions): () => void {
    const {
      id = uuid(),
      onClick,
      onReply,
      onAction,
      onFailed,
      onClose,
      onShow,
      ...attrs
    } = opts
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.app.electron?.notification.show?.({
      id,
      ...attrs,
      silent: true,
    })
    this.nativeHandlers.set(id, { onClick, onReply, onAction, onFailed, onClose, onShow })
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- UXP-3744 - Fix Promise-related ESLint issues
    return () => id && this.app.electron?.notification.close?.(id)
  }

  private showWeb(opts: NotificationOptions): () => void {
    const { id = uuid(), onClick, onClose, onShow, title, body } = opts
    const notification = new Notification(title, {
      body,
      tag: id,
      silent: true,
    })
    notification.onclick = onClick ?? notification.onclick
    notification.onclose = onClose ?? notification.onclose
    notification.onshow = onShow ?? notification.onshow
    return () => notification.close()
  }

  private playSound(opts: NotificationOptions) {
    const { soundName, silent } = opts

    if (silent || !this.playNotificationSounds) {
      return
    }

    switch (soundName) {
      case 'default':
        return sound.play('secondaryNotification')
      case 'missed-call':
        return sound.play('missedCall')
      case 'new-message':
        return sound.play('newMessage')
      default:
        return
    }
  }

  private registerListeners() {
    if (!this.app.isElectron) {
      return
    }
    this.app.electron?.on('notification', (event) => {
      switch (event.type) {
        case 'click':
          return this.nativeHandlers.get(event.id)?.onClick?.()
        case 'action':
          return this.nativeHandlers.get(event.id)?.onAction?.(event.index)
        case 'close':
          return this.nativeHandlers.get(event.id)?.onClose?.()
        case 'failed':
          return this.nativeHandlers.get(event.id)?.onFailed?.(event.error)
        case 'reply':
          return this.nativeHandlers.get(event.id)?.onReply?.(event.reply)
        case 'show':
          return this.nativeHandlers.get(event.id)?.onShow?.()
      }
    })
  }
}
